import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box, Typography, Grid, Paper,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';

const RING_SIZE = 150;
const RING_THICKNESS = 4.5;

const useStyles = makeStyles((theme) => ({

  // ── Section wrapper ───────────────────────────────────────────────────────
  root: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontSize: theme.typography.pxToRem(21),
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(3),
  },

  // ── Outer wrapper card ────────────────────────────────────────────────────
  outerCard: {
    borderRadius: 14,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    boxShadow: '0 2px 14px rgba(0,0,0,0.07)',
  },

  // ── Dimension card ────────────────────────────────────────────────────────
  card: {
    background: theme.palette.grey[50],
    borderRadius: 16,
    padding: theme.spacing(3, 2, 2.5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    border: '2px solid transparent',
    transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.15s',
    cursor: 'pointer',
    height: '100%',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 20px rgba(0,0,0,0.11)',
    },
  },
  cardActive: {
    borderColor: theme.palette.common.gamsPetroleumBlue || '#2a5b77',
    boxShadow: '0 4px 22px rgba(42,91,119,0.18)',
    background: '#fff',
  },

  // ── Ring ──────────────────────────────────────────────────────────────────
  ringWrap: {
    position: 'relative',
    width: RING_SIZE,
    height: RING_SIZE,
    marginBottom: theme.spacing(2),
  },
  ringBg: {
    color: theme.palette.grey[200],
    position: 'absolute',
    top: 0,
    left: 0,
  },
  ringFg: {
    position: 'absolute',
    top: 0,
    left: 0,
    transition: 'color 0.3s',
    '& circle': {
      strokeLinecap: 'round',
    },
  },
  ringFgAwarded: {
    color: theme.palette.common.gamsOrange || '#f5a623',
  },
  ringFgPending: {
    color: theme.palette.grey[400],
  },
  ringInner: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ringPercent: {
    fontSize: theme.typography.pxToRem(26),
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    lineHeight: 1,
    letterSpacing: '-0.01em',
  },
  cardTitle: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightMedium,
    textAlign: 'center',
    color: theme.palette.text.primary,
    lineHeight: 1.4,
    marginBottom: theme.spacing(0.75),
  },
  awardedBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    marginTop: theme.spacing(0.5),
  },
  awardedIcon: {
    fontSize: 15,
    color: theme.palette.common.gamsOrange || '#f5a623',
  },
  pendingIcon: {
    fontSize: 15,
    color: theme.palette.grey[400],
  },
  statusText: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: '0.04em',
  },
  statusTextAwarded: {
    color: theme.palette.common.gamsOrange || '#f5a623',
  },
  statusTextPending: {
    color: theme.palette.grey[400],
  },

  // ── Knowledge columns ─────────────────────────────────────────────────────
  panelCard: {
    borderRadius: 12,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    height: '100%',
  },
  colTitle: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2.5),
    letterSpacing: '-0.01em',
  },
  ucGroup: {
    marginBottom: theme.spacing(2),
    '&:last-child': { marginBottom: 0 },
  },
  ucGroupTitle: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(0.5),
    borderBottom: `2px solid ${theme.palette.grey[100]}`,
  },
  knowledgeItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(0.8, 0),
    borderBottom: `1px solid ${theme.palette.grey[50]}`,
    '&:last-child': { borderBottom: 'none' },
  },
  itemIndex: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.common.gamsPetroleumBlue || '#2a5b77',
    minWidth: 32,
    flexShrink: 0,
    paddingTop: 2,
    letterSpacing: '0.02em',
  },
  itemIndexSkill: {
    color: theme.palette.common.gamsOrange || '#f5a623',
  },
  knowledgeText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.primary,
    lineHeight: 1.5,
  },
  emptyText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
  },
}));

const DimensionCard = ({ item, isActive, onClick, classes }) => {
  const percent = Math.round((item.currentAverage || 0) * 100);

  return (
    <Box
      className={`${classes.card} ${isActive ? classes.cardActive : ''}`}
      onClick={onClick}
    >
      <div className={classes.ringWrap}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={RING_SIZE}
          thickness={RING_THICKNESS}
          className={classes.ringBg}
        />
        <CircularProgress
          variant="determinate"
          value={percent}
          size={RING_SIZE}
          thickness={RING_THICKNESS}
          className={`${classes.ringFg} ${item.badgeAwarded ? classes.ringFgAwarded : classes.ringFgPending}`}
        />
        <div className={classes.ringInner}>
          <Typography className={classes.ringPercent}>{percent}%</Typography>
        </div>
      </div>

      <Typography className={classes.cardTitle}>{item.dimensionName}</Typography>

      <div className={classes.awardedBadge}>
        {item.badgeAwarded ? (
          <>
            <CheckCircleIcon className={classes.awardedIcon} />
            <Typography className={`${classes.statusText} ${classes.statusTextAwarded}`}>Awarded</Typography>
          </>
        ) : (
          <>
            <RadioButtonUncheckedIcon className={classes.pendingIcon} />
            <Typography className={`${classes.statusText} ${classes.statusTextPending}`}>In progress</Typography>
          </>
        )}
      </div>
    </Box>
  );
};

const KnowledgeColumns = ({ skillProgress, activeIndex, classes }) => {
  const source = activeIndex !== null ? [skillProgress[activeIndex]] : skillProgress;

  // Group knowledge items by UC (dimensionName), preserving only UCs that have items of that type
  const knowledgeByUc = source
    .map(item => ({
      name: item.dimensionName,
      items: (item.knowledgeItems || []).filter(k => k.type === 'Conoscenza'),
    }))
    .filter(uc => uc.items.length > 0);

  const skillsByUc = source
    .map(item => ({
      name: item.dimensionName,
      items: (item.knowledgeItems || []).filter(k => k.type === 'Capacità'),
    }))
    .filter(uc => uc.items.length > 0);

  if (knowledgeByUc.length === 0 && skillsByUc.length === 0) return null;

  return (
    <Grid container spacing={3} alignItems="stretch">
      <Grid item xs={12} sm={6}>
        <Paper className={classes.panelCard} elevation={1}>
          <Typography className={classes.colTitle}>Knowledge</Typography>
          {knowledgeByUc.length === 0 ? (
            <Typography className={classes.emptyText}>No knowledge items available.</Typography>
          ) : (
            knowledgeByUc.map(uc => (
              <div key={uc.name} className={classes.ucGroup}>
                <Typography className={classes.ucGroupTitle}>{uc.name}</Typography>
                {uc.items.map((k, i) => (
                  <div key={k.id} className={classes.knowledgeItem}>
                    {/*<Typography className={classes.itemIndex}>1.{i + 1}</Typography>*/}
                    <Typography className={classes.knowledgeText}>{k.name}</Typography>
                  </div>
                ))}
              </div>
            ))
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Paper className={classes.panelCard} elevation={1}>
          <Typography className={classes.colTitle}>Skills</Typography>
          {skillsByUc.length === 0 ? (
            <Typography className={classes.emptyText}>No skills available.</Typography>
          ) : (
            skillsByUc.map(uc => (
              <div key={uc.name} className={classes.ucGroup}>
                <Typography className={classes.ucGroupTitle}>{uc.name}</Typography>
                {uc.items.map((k, i) => (
                  <div key={k.id} className={classes.knowledgeItem}>
                    {/*<Typography className={`${classes.itemIndex} ${classes.itemIndexSkill}`}>1.{i + 1}</Typography>*/}
                    <Typography className={classes.knowledgeText}>{k.name}</Typography>
                  </div>
                ))}
              </div>
            ))
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

const QscSkillProgress = ({ skillProgress }) => {
  const classes = useStyles();
  const [activeIndex, setActiveIndex] = useState(0);

  if (!skillProgress || skillProgress.length === 0) return null;

  const handleCardClick = (index) => {
    setActiveIndex(prev => prev === index ? null : index);
  };

  return (
    <Box className={classes.root}>
      <Typography className={classes.sectionTitle}>
        Competency Unit Coverage
      </Typography>

      <Paper className={classes.outerCard} elevation={1}>
        <Grid container spacing={2} justifyContent="center">
          {skillProgress.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={item.dimensionId}>
              <DimensionCard
                item={item}
                isActive={activeIndex === index}
                onClick={() => handleCardClick(index)}
                classes={classes}
              />
            </Grid>
          ))}
        </Grid>
      </Paper>

      <KnowledgeColumns
        skillProgress={skillProgress}
        activeIndex={activeIndex}
        classes={classes}
      />
    </Box>
  );
};

export default QscSkillProgress;
